'use client';

import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react';
import { useObservable } from '@legendapp/state/react';
import { type Observable } from '@legendapp/state';
import { couponReducer, initialCouponState } from '../stores/coupon/coupon-reducer';
import type { CouponLoadingType, CouponState } from '../stores/coupon/actions/types';
import type { SalesRule } from '../../../types/front.graphql';
import { DisplayLocation } from '../../../types/marketing.graphql';

interface CouponProviderProps {
    children: React.ReactNode;
    /** Display location for voucher filtering (CART, CHECKOUT, WALLET) */
    displayLocation?: DisplayLocation;
}

interface CouponContextType {
    state: CouponState;
    selectedCoupon?: SalesRule;
    couponState$: Observable<CouponState>;
    setLoading: (type: CouponLoadingType, value: boolean) => void;
    /** Display location for voucher filtering */
    displayLocation: DisplayLocation;
}

export const CouponContext = createContext<CouponContextType | undefined>(undefined);

export const CouponProvider: React.FC<CouponProviderProps> = ({ children, displayLocation = DisplayLocation.CART }) => {
    const [state, dispatch] = useReducer(couponReducer, initialCouponState);

    const couponState$ = useObservable<CouponState>({
        selectedCoupon: undefined,
        selectedVoucherInfo: undefined,
    });

    const setLoading = useCallback(
        (type: CouponLoadingType, value: boolean) => {
            dispatch({
                type: 'SET_LOADING',
                payload: { type, value },
            });
        },
        [dispatch]
    );

    const contextValue = useMemo(
        () => ({
            state,
            couponState$,
            setLoading,
            displayLocation,
        }),
        [state, couponState$, setLoading, displayLocation]
    );

    return <CouponContext.Provider value={contextValue}>{children}</CouponContext.Provider>;
};

export const useCouponContext = () => {
    return useContext(CouponContext);
};
